"use client";

import { Card, CardContent } from "@/components/rac/card";
import { Avatar } from "@/components/radix/avatar";
import { cn } from "@/utils/cn";
import { Star, StarHalf } from "lucide-react";
import Image from "next/image";
import React from "react";
import { Testimonial } from ".";

interface TestimonialCardProps {
	testimonial: Testimonial;
	className?: string;
}

export const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial, className }) => {
	const renderStars = () => {
		return Array(5)
			.fill(0)
			.map((_, i) => {
				const isFilled = i < Math.floor(testimonial.rating);
				const isHalf = !isFilled && i < testimonial.rating;

				return (
					<span key={i} className="inline-block" role="img" aria-hidden="true">
						{isFilled ? (
							<Star className="w-4 h-4 fill-primary text-primary" />
						) : isHalf ? (
							<StarHalf className="w-4 h-4 fill-primary text-primary" />
						) : (
							<Star className="w-4 h-4 text-muted-foreground" />
						)}
					</span>
				);
			});
	};

	return (
		<Card className={cn("bg-background shadow-sm h-80 border", className)}>
			<CardContent className="p-4 h-full flex flex-col">
				<div className="flex items-center gap-3 mb-3">
					<Avatar className="w-10 h-10" aria-hidden="true">
						{testimonial.avatar ? (
							<Image
								src={testimonial.avatar}
								alt={testimonial.name}
								width={40}
								height={40}
								className="object-cover w-full h-full"
							/>
						) : (
							testimonial.name.charAt(0)
						)}
					</Avatar>
					<div>
						<div className="font-medium text-sm text-foreground">{testimonial.name}</div>
						<div className="flex items-center gap-1">
							<div role="img" aria-label={`Rated ${testimonial.rating} out of 5 stars`}>
								{renderStars()}
								<span className="sr-only">{testimonial.rating} out of 5 stars</span>
							</div>
							<span className="text-xs text-muted-foreground ml-1">{testimonial.timeAgo}</span>
						</div>
					</div>
				</div>
				{testimonial.summary && (
					<div className="flex-grow mb-2">
						<p className="text-muted-foreground text-sm font-bold" aria-live="polite" tabIndex={0}>
							{testimonial.summary}
						</p>
					</div>
				)}
				<div className="flex-grow overflow-hidden">
					<p
						className="text-muted-foreground text-sm h-full overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent hover:scrollbar-thumb-muted-foreground/40 pr-2"
						aria-live="polite"
						tabIndex={0}
					>
						<q>{testimonial.review}</q>
					</p>
				</div>
			</CardContent>
		</Card>
	);
};
